import clsx from "clsx"
import React, { createRef, useEffect, useMemo, useState } from "react"
import { Dialog, makeStyles } from "@material-ui/core"
import { Localized } from "@fluent/react"
import Screen from "shared/Components/Skeleton/Screen"
import { StandardTopbar, StandardTopbarTitle } from "shared/Components/Skeleton/TopBar"
import SlideUpTransition from "shared/Components/Dialog/SlideUpTransition"
import ErrorHandlerModal from "shared/Modules/Error/Components/ErrorHandlerModal"
import { HeaderSection, ReceiptContainer } from "../Components/ReceiptBlocks"
import { getPaymentMethodType, PurchaseDetailsSection } from "../Components/PurchaseDetailsSection"
import { DeliveryDetailsSection } from "../Components/DeliveryDetailsSection"
import { FlatOrderLineGroup, OrderDetailsSection } from "../Components/OrderDetailsSection"
import { ActionsSection } from "../Components/ActionsSection"
import { ProofOfPurchaseOrder } from "../orderDetailsTypes"
import {
    MEETING_DELIVERY_MOCK,
    MEETING_ORDERS_CUSTOMER_MOCK,
    MEETING_ORDERS_KITCHEN_MOCK,
    MEETING_ORDERS_MOCK,
    MEETING_ORDERS_PAYMENT
} from "../../../Static/MeetingPlanning/deliveryForm"
import { DateTime } from "luxon"
import { BuyerParty, IOrderLine, PaymentMethodType } from "../../../Types/appTypes"
import { COMPANY_PAYMENT_METHOD } from "../../Transaction/paymentLib"
import { useDateTime } from "../../Localization/useDateTime"

type DetailedReceiptModalProps = Readonly<{
    order: ProofOfPurchaseOrder
    open: boolean
    onClose: () => void
}>

const useStyles = makeStyles((theme) => ({
    scrollShadowBottom: {
        "&::after": {
            zIndex: 1,
            position: "fixed",
            content: '"-"',
            bottom: "-15px",
            left: "0",
            width: "100%",
            boxShadow: "0 -5px 25px rgb(0 0 0 / 18%)"
        }
    },
    edge: {
        height: "10px"
    }
}))

type DetailedReceiptProps = Readonly<{
    order: ProofOfPurchaseOrder
    classes: ReturnType<typeof useStyles>
    setIsAtBottom: (atBottom: boolean) => void
    onClose: () => void
}>

type GroupedOrderLines = Record<BuyerParty, Record<PaymentMethodType, IOrderLine[]>>

function getOrderLinePaymentMethod(line: IOrderLine, orderPaymentMethod: PaymentMethodType) {
    if (line.paymentDetails?.method) return line.paymentDetails.method
    if (line.buyerParty === BuyerParty.COMPANY) return COMPANY_PAYMENT_METHOD
    return getPaymentMethodType(orderPaymentMethod)
}

function getGroupedOrderLines() {
    const loadedOrder = MEETING_ORDERS_MOCK[0]
    // Todays orders does not contain payment methods for order
    if (!loadedOrder) return undefined

    const orderLines = loadedOrder.deliveries?.[0].orderLines
    if (!orderLines) return undefined

    if (!loadedOrder.paymentDetails?.method) return undefined
    const orderPaymentMethod = loadedOrder.paymentDetails.method

    return orderLines.reduce((acc, line) => {
        if (line.buyerParty) {
            const paymentMethod = getOrderLinePaymentMethod(line, orderPaymentMethod)

            let buyerPartyGroups = acc[line.buyerParty]
            if (!buyerPartyGroups) {
                buyerPartyGroups = {} as Record<PaymentMethodType, IOrderLine[]>
                acc[line.buyerParty] = buyerPartyGroups
            }

            let paymentMethodLines = buyerPartyGroups[paymentMethod]
            if (!paymentMethodLines) {
                paymentMethodLines = []
                buyerPartyGroups[paymentMethod] = paymentMethodLines
            }

            paymentMethodLines.push(line)
        }
        return acc
    }, {} as GroupedOrderLines)
}

function getIncludedVAT(totalPrice: number) {
    const dkVatRate = 0.25
    const vatMultiplier = 1 + dkVatRate
    const priceWithoutVat = totalPrice / vatMultiplier
    return totalPrice - priceWithoutVat
}

function flattenGroupedOrderLines(orderLines: GroupedOrderLines | undefined) {
    if (!orderLines) return undefined

    return Object.entries(orderLines).reduce((allGroups, [buyerParty, group]) => {
        return Object.entries(group).reduce((bpGroups, [paymentMethod, ol]) => {
            const totalPrice = ol.reduce((sum, line) => sum + (line.price?.amount ?? 0), 0)
            const includedVAT = getIncludedVAT(totalPrice)

            const flatGroup: FlatOrderLineGroup = {
                buyerParty: buyerParty as BuyerParty,
                paymentMethod: paymentMethod as PaymentMethodType,
                orderLines: ol,
                totalPrice,
                includedVAT,
            }
            bpGroups.push(flatGroup)
            return bpGroups
        }, allGroups)
    }, [] as FlatOrderLineGroup[])
}

function DetailedMeetingReceipt({ order, classes, setIsAtBottom, onClose }: DetailedReceiptProps) {
    const bottomEdge = createRef<HTMLDivElement>()
    const dateTimeFactory = useDateTime()

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            for (const intersection of entries) {
                if (intersection.target.id === "bottomEdge") {
                    setIsAtBottom(intersection.isIntersecting)
                }
            }
        }, {
            threshold: 1.0
        })

        if (bottomEdge.current) observer.observe(bottomEdge.current)

        return () => observer.disconnect()
    }, [])

    const groupedOrderLines = useMemo(
        () => flattenGroupedOrderLines(getGroupedOrderLines()),
        []
    )

    const displayName = order.displayName ?? `${order.id}`

    const topbar = (
        <StandardTopbar
            middleElement={
                <Localized id="receipt-title">
                    <StandardTopbarTitle>Kvittering</StandardTopbarTitle>
                </Localized>
            }
            onClose={onClose}
        />
    )

    return (
        <Screen
            name="orderReceiptDetailed"
            alternativeHeaderElement={topbar}
            containerPadding={0}
            fitPage
        >
            <ReceiptContainer>
                <HeaderSection>{displayName}</HeaderSection>

                <PurchaseDetailsSection
                    paymentDetails={MEETING_ORDERS_PAYMENT}
                    kitchen={MEETING_ORDERS_KITCHEN_MOCK}
                    customer={MEETING_ORDERS_CUSTOMER_MOCK}
                    timeOfPurchase={order.created ? dateTimeFactory.fromFNDateTime(order.created) : undefined}
                    orderId={`${order.id}`}
                    shopChannel={'GoPay app (web)'}
                />

                <ActionsSection
                    orderId={order.id}
                    orderUid={order.uid}
                    delivery={MEETING_DELIVERY_MOCK}
                />

                <DeliveryDetailsSection
                    orderType={order.orderType}
                    delivery={MEETING_DELIVERY_MOCK}
                    deliveryComment={{
                        present: true,
                        text: "Occurs every day, 11 times, running from April 11th, 2025, from 4:30 PM to 5:30 PM."
                    }}
                />

                {groupedOrderLines ? groupedOrderLines.map((group, index, array) => (
                    <OrderDetailsSection key={index} orderDetails={group} final={index === array.length - 1} />
                )) : null}
            </ReceiptContainer>

            <div ref={bottomEdge} id="bottomEdge" className={classes.edge}></div>
        </Screen>
    )
}

export default function DetailedResourceReceiptModal({ order, open, onClose }: DetailedReceiptModalProps) {
    const classes = useStyles()
    const [isAtBottom, setIsAtBottom] = useState(false)

    return (
        <Dialog open={open} TransitionComponent={SlideUpTransition} fullScreen scroll="paper" PaperProps={{ elevation: 0 }} classes={{ paperScrollPaper: clsx({ [classes.scrollShadowBottom]: !isAtBottom}) }}>
            <ErrorHandlerModal close={onClose}>
                <DetailedMeetingReceipt order={order} classes={classes} setIsAtBottom={setIsAtBottom} onClose={onClose} />
            </ErrorHandlerModal>
        </Dialog>
    )
}
